import { Link, Text, Tooltip } from '@nike/eds'
import clsx from 'clsx'
import './Chips.css'
import Chip from '@mui/material/Chip'
import { SetStateAction, useState } from 'react'
import { textInputToSearchParams } from '../../hooks/QueryParamsHook'
import { getDateValueInTimezone, isDateString } from '../../utils/DateUtils'

export interface ChipValue {
  field: string
  value: any
  stringValue: string
  hoverValue?: string
  index?: number
}

export interface ChipsProps {
  inputState: Record<string, string>
  setInputState: SetStateAction<any>
  setQueryParameters: (object: any) => void
  setSelectedCarrierHubs?: SetStateAction<any>
  clearInputState: () => void
  className?: string
}

export const Chips = ({
  inputState,
  setInputState,
  setQueryParameters,
  setSelectedCarrierHubs,
  clearInputState,
  className = '',
}: ChipsProps) => {
  const MAX_CHIPS_LENGTH = 5
  const enrichedClassName = clsx('filter-chips', className)
  const [showAllPacklists, setShowAllPacklists] = useState<boolean>(false)
  const [showAllSalesOrders, setShowAllSalesOrders] = useState<boolean>(false)
  const [showAllDeliveryNotes, setShowAllDeliveryNotes] = useState<boolean>(false)
  const [showAllGoodsHolderNumbers, setShowAllGoodsHolderNumbers] = useState<boolean>(false)
  const [showAllProNumbers, setShowAllProNumbers] = useState<boolean>(false)
  const [showAllVendorAccessNumbers, setShowAllVendorAccessNumbers] = useState<boolean>(false)
  const [showAllTransportHolderNumbers, setShowAllTransportHolderNumbers] = useState<boolean>(false)
  const [showAllCalculatedShipmentStatusCodes, setShowAllCalculatedShipmentStatusCodes] =
    useState<boolean>(false)
  const [showAllCarierHubs, setShowAllCarrierHubs] = useState<boolean>(false)
  const [showAllShipToNumbers, setShowAllShipToNumbers] = useState<boolean>(false)
  const [showAllShipToCountries, setShowAllShipToCountries] = useState<boolean>(false)
  const [showAllShippingPoints, setShowAllShippingPoints] = useState<boolean>(false)
  const [showAllOrderTypeCodes, setShowAllOrderTypeCodes] = useState<boolean>(false)
  const [showActualShippedTimestampFrom, setShowActualShippedTimestampFrom] =
    useState<boolean>(false)
  const [showActualShippedTimestampTo, setShowActualShippedTimestampTo] = useState<boolean>(false)
  const [showActualDeliveryDateFrom, setShowActualDeliveryDateFrom] = useState<boolean>(false)
  const [showActualDeliveryDateTo, setShowActualDeliveryDateTo] = useState<boolean>(false)
  const [showTransportHolderLicensePlateNumbers, setShowTransportHolderLicensePlateNumbers] =
    useState<boolean>(false)
  const [showTransportHolderIdReferenceNumbers, setShowTransportHolderIdReferenceNumbers] =
    useState<boolean>(false)
  const [showAllShipToNames, setShowAllShipToNames] = useState<boolean>(false)

  const [showHasTTExceptions, setShowHasTTExceptions] = useState<boolean>(false)

  const makeChipsArray = (filter: any = {}): ChipValue[] => {
    const chipsArrayFormat = (field: string, value: any) => {
      switch (field) {
        case 'calculatedShipmentStatusCodes':
          return `Status: ${value}`
        case 'packlistNumbers':
          return `Packlist: ${value}`
        case 'salesOrderNumbers':
          return `Sales Order: ${value}`
        case 'deliveryNoteNumbers':
          return `Delivery Note: ${value}`
        case 'goodsHolderNumbers':
          return `Carton / Tracking Number: ${value}`
        case 'proNumbers':
          return `CMR: ${value}`
        case 'vendorAccessNumbers':
          return `Trailer Access Number: ${value}`
        case 'transportHolderNumbers':
          return `Trailer Number: ${value}`
        case 'carrierHubs':
          return `${value}`
        case 'shipToCodes':
          return `ShipTo Number: ${value}`
        case 'shipToCountryCodes':
          return `ShipTo Country: ${value}`
        case 'shippingPointCodes':
          return `Shippingpoint: ${value}`
        case 'orderTypeCodes':
          return `Order Type Code: ${value}`
        case 'actualShippedTimestampFrom':
          return `Shipped timestamp from: ${getDateValueInTimezone(
            value,
            'Europe/Brussels',
            'yyyy-MM-dd'
          )}`
        case 'actualShippedTimestampTo':
          return `Shipped timestamp to: ${getDateValueInTimezone(
            value,
            'Europe/Brussels',
            'yyyy-MM-dd'
          )}`
        case 'actualDeliveryDateFrom':
          return `Actual Delivery Date from: ${getDateValueInTimezone(
            value,
            'Europe/Brussels',
            'yyyy-MM-dd'
          )}`
        case 'actualDeliveryDateTo':
          return `Actual Delivery Date to: ${getDateValueInTimezone(
            value,
            'Europe/Brussels',
            'yyyy-MM-dd'
          )}`
        case 'transportHolderLicensePlateNumbers':
          return `License Plate Number: ${value}`
        case 'transportHolderIdReferenceNumbers':
          return `Trailer ID Refs: ${value}`
        case 'shipToNames':
          return `Ship To Name: ${value}`
        case 'hasTTExceptions':
          return `Has T&T Exceptions`
        default:
          return value
      }
    }

    return Object.keys(filter)
      .flatMap((field) => {
        const filterValue = filter[field]
          ? Array.isArray(filter[field]) ||
            isDateString(filter[field]) ||
            typeof filter[field] == 'boolean' ||
            field === 'search' ||
            field === 'shipToNames'
            ? filter[field]
            : filter[field].trim().split(/[\s:,;\n]+/)
          : []
        if (Array.isArray(filterValue)) {
          return filterValue.map((value, index) => ({
            field,
            value,
            index,
            stringValue: chipsArrayFormat(field, value),
          }))
        }
        let stringValue = chipsArrayFormat(field, filterValue)
        return stringValue !== undefined
          ? [{ field, value: filterValue, stringValue: stringValue }]
          : (undefined as unknown as ChipValue[])
      })
      .filter((it) => it?.value !== undefined)
  }

  const allValues = makeChipsArray(inputState)

  const searchChips = allValues.filter((e) => e?.field === 'search')
  const packlistChips = allValues.filter((e) => e?.field === 'packlistNumbers')
  const salesOrderChips = allValues.filter((e) => e?.field === 'salesOrderNumbers')
  const deliveryNoteChips = allValues.filter((e) => e?.field === 'deliveryNoteNumbers')
  const goodsHolderChips = allValues.filter((e) => e?.field === 'goodsHolderNumbers')
  const proNumberChips = allValues.filter((e) => e?.field === 'proNumbers')
  const vendorAccessNumberChips = allValues.filter((e) => e?.field === 'vendorAccessNumbers')
  const transportHolderNumberChips = allValues.filter((e) => e?.field === 'transportHolderNumbers')
  const statusChips = allValues.filter((e) => e?.field === 'calculatedShipmentStatusCodes')
  const carrierHubChips = allValues.filter((e) => e?.field === 'carrierHubs')
  const shipToCodeChips = allValues.filter((e) => e?.field === 'shipToCodes')
  const shipToCountryChips = allValues.filter((e) => e?.field === 'shipToCountryCodes')
  const shippingPointChips = allValues.filter((e) => e?.field === 'shippingPointCodes')
  const orderTypeCodeChips = allValues.filter((e) => e?.field === 'orderTypeCodes')
  const actualShippedTimestampFromChip = allValues.filter(
    (e) => e?.field === 'actualShippedTimestampFrom'
  )
  const actualShippedTimestampToChip = allValues.filter(
    (e) => e?.field === 'actualShippedTimestampTo'
  )
  const actualDeliveryDateFromChip = allValues.filter((e) => e?.field === 'actualDeliveryDateFrom')
  const actualDeliveryDateToChip = allValues.filter((e) => e?.field === 'actualDeliveryDateTo')
  const transportHolderLicensePlateNumbersChip = allValues.filter(
    (e) => e?.field === 'transportHolderLicensePlateNumbers'
  )
  const transportHolderIdReferenceNumbersChip = allValues.filter(
    (e) => e?.field === 'transportHolderIdReferenceNumbers'
  )
  const shipToNamesChip = allValues.filter((e) => e?.field === 'shipToNames')
  const hasTTExceptionsChip = allValues.filter((e) => e?.field === 'hasTTExceptions')

  const handleChipDelete = (value: ChipValue, inputState: any) => {
    const currentValue = inputState?.[value.field]
    if (currentValue === undefined || inputState === undefined) {
      return
    }
    let newValue
    if (currentValue && (isDateString(currentValue) || value.field === 'search')) {
      newValue = undefined
    } else if (currentValue && !Array.isArray(currentValue)) {
      const values =
        value.field === 'shipToNames'
          ? undefined
          : currentValue
              .trim()
              .split(/[\s:,;\n]+/)
              .filter((v, i) => i !== value.index)
      newValue = values?.length !== 0 ? values : undefined
    } else {
      newValue =
        currentValue.length > 1 ? currentValue.filter((v, i) => i !== value.index) : undefined
    }
    const newInputState = {
      ...inputState,
      [value.field]: newValue,
    }

    setInputState(newInputState)
    if (setSelectedCarrierHubs) {
      setSelectedCarrierHubs(newInputState.carrierHubs)
    }
    setQueryParameters({
      searchParameters: textInputToSearchParams(newInputState),
      paging: { from: 0 },
    })
  }

  const createChipsArray = (values: ChipValue[], show: boolean, showAll, inputState: any) => {
    return (
      <div>
        {values
          .slice(0, show ? values.length : MAX_CHIPS_LENGTH)
          .map((item: ChipValue, key: number) => {
            return item.hoverValue ? (
              <Tooltip
                bodySlot={
                  <div>
                    <Text>{item.hoverValue}</Text>
                  </div>
                }
                isDark={true}
                placement="right"
              >
                <Chip
                  className="customChip"
                  variant="filled"
                  style={{ backgroundColor: 'var(--eds-color-background-default)' }}
                  label={item.stringValue}
                  key={`${item.field}-${key}`}
                  onDelete={() => {
                    handleChipDelete(item, inputState)
                  }}
                />
              </Tooltip>
            ) : (
              <Chip
                className="customChip"
                variant="filled"
                style={{ backgroundColor: 'var(--eds-color-background-default)' }}
                label={item.stringValue}
                key={`${item.field}-${key}`}
                onDelete={() => {
                  handleChipDelete(item, inputState)
                }}
              />
            )
          })}
        {show && values.length > MAX_CHIPS_LENGTH && (
          <Chip
            onClick={() => showAll(false)}
            label={'See Less'}
            variant="outlined"
            style={{ margin: 'var(--eds-space-4)' }}
          />
        )}
        {!show && values.length > MAX_CHIPS_LENGTH && (
          <Chip
            onClick={() => showAll(true)}
            label={`+${values.length - MAX_CHIPS_LENGTH}`}
            variant="outlined"
            style={{ margin: 'var(--eds-space-4)' }}
          />
        )}
      </div>
    )
  }

  const createSearchChip = () => {
    const value = searchChips.map((c) => c.value).join(' ')
    const stringValue = `🔎 ${value}`.substring(0, 120) + '...'
    const hoverValue = value
    return createChipsArray(
      [{ field: 'search', value, stringValue, hoverValue }],
      false,
      null,
      inputState
    )
  }
  return (
    <div className={enrichedClassName}>
      <div className="filter-chips-action">
        <Text font="title-5" className="eds-spacing--mb-8">
          Filters ({allValues?.length})
        </Text>
        <Link as="button" variant="secondary" onClick={clearInputState}>
          Clear All
        </Link>
      </div>

      <div className="filter-chips-group">
        {searchChips.length > 0 && createSearchChip()}

        {packlistChips.length > 0 &&
          createChipsArray(packlistChips, showAllPacklists, setShowAllPacklists, inputState)}

        {salesOrderChips.length > 0 &&
          createChipsArray(salesOrderChips, showAllSalesOrders, setShowAllSalesOrders, inputState)}

        {deliveryNoteChips.length > 0 &&
          createChipsArray(
            deliveryNoteChips,
            showAllDeliveryNotes,
            setShowAllDeliveryNotes,
            inputState
          )}

        {goodsHolderChips.length > 0 &&
          createChipsArray(
            goodsHolderChips,
            showAllGoodsHolderNumbers,
            setShowAllGoodsHolderNumbers,
            inputState
          )}

        {proNumberChips.length > 0 &&
          createChipsArray(proNumberChips, showAllProNumbers, setShowAllProNumbers, inputState)}

        {vendorAccessNumberChips.length > 0 &&
          createChipsArray(
            vendorAccessNumberChips,
            showAllVendorAccessNumbers,
            setShowAllVendorAccessNumbers,
            inputState
          )}

        {transportHolderNumberChips.length > 0 &&
          createChipsArray(
            transportHolderNumberChips,
            showAllTransportHolderNumbers,
            setShowAllTransportHolderNumbers,
            inputState
          )}

        {statusChips.length > 0 &&
          createChipsArray(
            statusChips,
            showAllCalculatedShipmentStatusCodes,
            setShowAllCalculatedShipmentStatusCodes,
            inputState
          )}

        {carrierHubChips.length > 0 &&
          createChipsArray(carrierHubChips, showAllCarierHubs, setShowAllCarrierHubs, inputState)}

        {shipToCodeChips.length > 0 &&
          createChipsArray(
            shipToCodeChips,
            showAllShipToNumbers,
            setShowAllShipToNumbers,
            inputState
          )}

        {shipToCountryChips.length > 0 &&
          createChipsArray(
            shipToCountryChips,
            showAllShipToCountries,
            setShowAllShipToCountries,
            inputState
          )}

        {shippingPointChips.length > 0 &&
          createChipsArray(
            shippingPointChips,
            showAllShippingPoints,
            setShowAllShippingPoints,
            inputState
          )}

        {orderTypeCodeChips.length > 0 &&
          createChipsArray(
            orderTypeCodeChips,
            showAllOrderTypeCodes,
            setShowAllOrderTypeCodes,
            inputState
          )}

        {actualShippedTimestampFromChip.length > 0 &&
          createChipsArray(
            actualShippedTimestampFromChip,
            showActualShippedTimestampFrom,
            setShowActualShippedTimestampFrom,
            inputState
          )}

        {actualShippedTimestampToChip.length > 0 &&
          createChipsArray(
            actualShippedTimestampToChip,
            showActualShippedTimestampTo,
            setShowActualShippedTimestampTo,
            inputState
          )}

        {actualDeliveryDateFromChip.length > 0 &&
          createChipsArray(
            actualDeliveryDateFromChip,
            showActualDeliveryDateFrom,
            setShowActualDeliveryDateFrom,
            inputState
          )}

        {actualDeliveryDateToChip.length > 0 &&
          createChipsArray(
            actualDeliveryDateToChip,
            showActualDeliveryDateTo,
            setShowActualDeliveryDateTo,
            inputState
          )}

        {hasTTExceptionsChip.length > 0 &&
          createChipsArray(
            hasTTExceptionsChip,
            showHasTTExceptions,
            setShowHasTTExceptions,
            inputState
          )}

        {transportHolderLicensePlateNumbersChip.length > 0 &&
          createChipsArray(
            transportHolderLicensePlateNumbersChip,
            showTransportHolderLicensePlateNumbers,
            setShowTransportHolderLicensePlateNumbers,
            inputState
          )}

        {transportHolderIdReferenceNumbersChip.length > 0 &&
          createChipsArray(
            transportHolderIdReferenceNumbersChip,
            showTransportHolderIdReferenceNumbers,
            setShowTransportHolderIdReferenceNumbers,
            inputState
          )}

        {shipToNamesChip.length > 0 &&
          createChipsArray(shipToNamesChip, showAllShipToNames, setShowAllShipToNames, inputState)}
      </div>
    </div>
  )
}

export default Chips
